// A hackathon set up before signing in, kept in this browser until it's created. Pure, so it's tested in client/test;
// the storage itself is in services/hackathonDraft.service.
import dayjs from 'dayjs';
import type { EditHackathonData } from '../components/Hackathon/HackathonEditor';

export interface HackathonDraft {
    data: EditHackathonData;
    // "Sign in to create" was pressed: create it as soon as the person is back, signed in
    createAfterSignIn: boolean;
}

const VERSION = 1;
const DATE_FIELDS = ['startDate', 'endDate', 'ideaSubmissionDeadline'] as const;

export const serializeDraft = ({ data, createAfterSignIn }: HackathonDraft) => JSON.stringify({
    version: VERSION,
    createAfterSignIn,
    data: {
        ...data,
        startDate: data.startDate?.toISOString() ?? null,
        endDate: data.endDate?.toISOString() ?? null,
        ideaSubmissionDeadline: data.ideaSubmissionDeadline?.toISOString() ?? null
    }
});

/** A saved draft, or null when there's none or it can't be read (another version, or tampered with). */
export const parseDraft = (raw: string | null, blank: EditHackathonData): HackathonDraft | null => {
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.version !== VERSION || !parsed.data || typeof parsed.data !== 'object') {
            return null;
        }
        // fields added since it was saved start blank
        const data: EditHackathonData = { ...blank, ...parsed.data };
        DATE_FIELDS.forEach(field => {
            const value = parsed.data[field];
            data[field] = value && dayjs(value).isValid() ? dayjs(value) : null;
        });
        return { data, createAfterSignIn: parsed.createAfterSignIn === true };
    } catch (e) {
        return null;
    }
};
